import React, { useState } from "react";
import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, Divider, Dropdown, Empty, Form, Input, message, Modal, Space, Tag, Timeline, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { productClientActions } from "@fitness-app/app-store";
import { type ProductClientNote } from "@fitness-app/data-models/entities/ProductClient";

import { useAppDispatch } from "~/store/initializeStore";

interface ProductClientNotesProps {
  productClientId: string;
  notes: ProductClientNote[] | null | undefined;
  readonly?: boolean;
}

/**
 * Audit notes on a product client (TRE-1511). System flows (installments
 * payoff) append entries so the timeline explains non-obvious state — e.g. a
 * "paid" plan with a canceled Stripe subscription. Admins add manual notes
 * through the form below the timeline and manage them from the row dropdown
 * (system notes are delete-only — their content is an audit record).
 */
const ProductClientNotes = ({ productClientId, notes, readonly }: ProductClientNotesProps) => {
  const { t } = useTranslation(["client", "common"]);
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<{ content: string }>();
  const [saving, setSaving] = useState(false);
  const [editedNote, setEditedNote] = useState<ProductClientNote | null>(null);
  const [editedContent, setEditedContent] = useState("");

  const sortedNotes = [...(notes ?? [])].sort((first, second) => second.createdAt.localeCompare(first.createdAt));

  const handleAddNote = async ({ content }: { content: string }) => {
    if (!content?.trim()) {
      return;
    }

    setSaving(true);

    await dispatch(
      productClientActions.addProductClientNote({
        payload: { productClientId, content },
        onSuccess: () => {
          form.resetFields();
          void message.success(t("client:notes.added"));
        },
        onFailure: () => {
          void message.error(t("common:messages.error.default"));
        },
      }),
    );

    setSaving(false);
  };

  const handleUpdateNote = async () => {
    if (!editedNote || !editedContent.trim()) {
      return;
    }

    setSaving(true);

    await dispatch(
      productClientActions.updateProductClientNote({
        payload: { productClientId, noteId: editedNote.id, content: editedContent },
        onSuccess: () => {
          setEditedNote(null);
          void message.success(t("client:notes.updated"));
        },
        onFailure: () => {
          void message.error(t("common:messages.error.default"));
        },
      }),
    );

    setSaving(false);
  };

  const confirmDeleteNote = (note: ProductClientNote) => {
    Modal.confirm({
      title: t("client:notes.deleteConfirmTitle"),
      content: note.content.length > 120 ? `${note.content.slice(0, 120)}…` : note.content,
      okText: t("client:notes.deleteConfirmOk"),
      okButtonProps: { danger: true },
      cancelText: t("common:button.cancel"),
      onOk: async () => {
        await dispatch(
          productClientActions.deleteProductClientNote({
            payload: { productClientId, noteId: note.id },
            onSuccess: () => {
              void message.success(t("client:notes.deleted"));
            },
            onFailure: () => {
              void message.error(t("common:messages.error.default"));
            },
          }),
        );
      },
    });
  };

  const renderNote = (note: ProductClientNote) => {
    const isEdited = editedNote?.id === note.id;
    // System notes are audit records — editing them would falsify history.
    const canEdit = note.author !== "system";

    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Typography.Text type="secondary">{dayjs(note.createdAt).format("DD.MM.YYYY HH:mm")}</Typography.Text>
          {note.kind ? <Tag color="gold">{t(`client:notes.kind.${note.kind}`, note.kind)}</Tag> : null}
          <Typography.Text type="secondary">
            {note.author === "system" ? t("client:notes.systemAuthor") : note.author}
          </Typography.Text>
          {note.updatedAt ? (
            <Typography.Text type="secondary" italic>
              {t("client:notes.edited", { date: dayjs(note.updatedAt).format("DD.MM.YYYY HH:mm") })}
            </Typography.Text>
          ) : null}
          {!readonly && !isEdited ? (
            <Dropdown
              menu={{
                items: [
                  ...(canEdit
                    ? [
                        {
                          key: "edit",
                          icon: <EditOutlined />,
                          label: t("client:notes.edit"),
                          onClick: () => {
                            setEditedNote(note);
                            setEditedContent(note.content);
                          },
                        },
                      ]
                    : []),
                  {
                    key: "delete",
                    icon: <DeleteOutlined />,
                    label: t("client:notes.delete"),
                    danger: true,
                    onClick: () => confirmDeleteNote(note),
                  },
                ],
              }}
              placement="bottomRight"
              trigger={["click"]}
            >
              <Button type="text" size="small" icon={<MoreOutlined />} />
            </Dropdown>
          ) : null}
        </div>
        {isEdited ? (
          <div className="space-y-2">
            <Input.TextArea
              rows={3}
              maxLength={2000}
              showCount
              value={editedContent}
              onChange={(event) => setEditedContent(event.target.value)}
              disabled={saving}
            />
            <Space>
              <Button
                type="primary"
                size="small"
                loading={saving}
                disabled={!editedContent.trim()}
                onClick={() => void handleUpdateNote()}
              >
                {t("common:button.save")}
              </Button>
              <Button size="small" disabled={saving} onClick={() => setEditedNote(null)}>
                {t("common:button.cancel")}
              </Button>
            </Space>
          </div>
        ) : (
          <Typography.Text>{note.content}</Typography.Text>
        )}
      </div>
    );
  };

  return (
    <div>
      <Divider orientation="left">{t("client:notes.title")}</Divider>
      {/* Full-width notes are unreadable on wide screens — cap the line length. */}
      <div className="max-w-3xl">
        {sortedNotes.length ? (
          <Timeline items={sortedNotes.map((note) => ({ key: note.id, children: renderNote(note) }))} />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("client:notes.empty")} />
        )}

        {!readonly && (
          <Form form={form} onFinish={handleAddNote} className="mt-4">
            <Form.Item name="content" rules={[{ required: true, message: t<string>("client:notes.contentRequired") }]}>
              <Input.TextArea
                rows={3}
                maxLength={2000}
                showCount
                placeholder={t<string>("client:notes.placeholder")}
                disabled={saving}
              />
            </Form.Item>
            <Form.Item className="mb-0">
              <Button type="primary" htmlType="submit" loading={saving}>
                {t("client:notes.addButton")}
              </Button>
            </Form.Item>
          </Form>
        )}
      </div>
    </div>
  );
};

export default ProductClientNotes;
